/**
 * Kiểu dữ liệu danh mục model AI cấp nền tảng (ADR-0083): `/api/v1/ai-models` và
 * `/api/v1/tenants/{id}/ai-models` của tenant-service, schema `AiModel*`, `TenantAiModel*` trong
 * `openapi/platform-api.yaml`. Tên trường giữ `snake_case` của API.
 *
 * `AiModelOption` là hợp đồng của module `ocr` (`GET /api/v1/m/ocr/ai-models/options`): người dùng
 * tenant chọn model khi cấu hình loại chứng từ và khi nhận dạng, đọc từ danh mục hiệu lực của tenant.
 */

/** Bộ adapter đã cài trong module (`AI_ENGINES` của `ai/engines.ts`). */
export const AI_ENGINES = [
  'GEMINI',
  'CLAUDE',
  'FOXAI',
  'GLM',
  'CHANDRA',
  'DEEPSEEK',
  'CHANDRA2',
  'PADDLE',
  'CHANDRA2_L40',
  'QWEN38',
  'CHANDRA2_H200',
  'QWEN38_H200',
  'GEMMA4',
  // ADR-0126: nhận dạng giọng nói, `muc_dich = GIONG`, chỉ đi qua đường âm thanh của cổng AI.
  'WHISPER',
] as const;
export type AiEngine = (typeof AI_ENGINES)[number];

export const THINKING_MODES = ['OPTIONAL', 'FORCED', 'DISABLED'] as const;
export type ThinkingMode = (typeof THINKING_MODES)[number];

/** `VAN_BAN`: chọn được để suy luận. `GIONG`: chỉ cổng AI tra theo khoá cho giọng đọc, không làm mặc định. */
export const MUC_DICH_MODEL = ['VAN_BAN', 'GIONG'] as const;
export type MucDichModel = (typeof MUC_DICH_MODEL)[number];

export interface AiModel {
  id: string;
  /** Tự sinh từ model id hoặc tên, không đổi; cấu hình chứng từ của module tham chiếu theo khoá này. */
  key: string;
  label: string;
  engine: AiEngine;
  /** `null` = model mặc định của endpoint (biến `OCR_AI_<ENGINE>_MODEL` do platform tiêm). */
  model_id: string | null;
  vision_model_id: string | null;
  thinking_mode: ThinkingMode;
  description: string | null;
  is_active: boolean;
  is_default: boolean;
  display_order: number;
  /**
   * Ba hệ số token Fox (ADR-0123 mục 2), mỗi hệ số 0,001–1000, tối đa 3 chữ số lẻ. Chỉ Quản trị nền tảng thấy.
   * Token Fox đầu vào = ceil(đầu vào thường × `he_so_dau_vao` + đọc cache × `he_so_cache`); đầu ra = ceil(ra × `he_so_dau_ra`).
   */
  he_so_dau_vao: number;
  /** `null` là bằng `he_so_dau_vao`. */
  he_so_cache: number | null;
  he_so_dau_ra: number;
  /** Token Fox cho 1 phút âm thanh, chỉ engine `WHISPER` có (ADR-0126 mục 3); ba hệ số của engine này cố định, không dùng. */
  token_fox_moi_phut: number | null;
  /** Tên model thật để tra Bảng giá model; `null` thì dùng `model_id`. */
  ten_model_gia: string | null;
  muc_dich: MucDichModel;
  created_at: string;
  updated_at: string;
}

/** `GET /ai-models/options`: chỉ model đang bật, kèm vai làm được ở chế độ tách hai bước. */
export interface AiModelOption {
  key: string;
  label: string;
  engine: AiEngine;
  thinking_mode: ThinkingMode;
  is_default: boolean;
  description: string | null;
  /** Đọc riêng từng trang được (bước 1 của chế độ tách, kho bản OCR). */
  can_read_pages: boolean;
  /** Điền dữ liệu vào trường, bảng được (bước 2, chế độ một model). */
  can_map: boolean;
}

/** Thân `POST /api/v1/ai-models`. Không có `key`: server sinh từ model id hoặc tên (ADR-0083 mục 2). */
export interface AiModelCreate {
  label: string;
  engine: AiEngine;
  model_id?: string | null;
  vision_model_id?: string | null;
  thinking_mode?: ThinkingMode;
  description?: string | null;
  is_active?: boolean;
  is_default?: boolean;
  display_order?: number;
  /** Bỏ trống là 1. */
  he_so_dau_vao?: number;
  /** Bỏ trống hoặc `null` là bằng `he_so_dau_vao`. */
  he_so_cache?: number | null;
  /** Bỏ trống là 1. */
  he_so_dau_ra?: number;
  /** Chỉ và bắt buộc với engine `WHISPER`. */
  token_fox_moi_phut?: number;
  ten_model_gia?: string | null;
  /** Bỏ trống là `VAN_BAN`. */
  muc_dich?: MucDichModel;
}

/** Trạng thái một dòng gợi ý hệ số (`AiModelMultiplierSuggestionStatus`). */
export type TrangThaiGoiYHeSo =
  | 'OK'
  | 'NO_PRICE_MODEL_NAME'
  | 'NO_PRICE'
  | 'NO_EXCHANGE_RATE'
  | 'NO_UNIT_PRICE'
  | 'PRICING_UNAVAILABLE';

/**
 * Một dòng `GET /api/v1/ai-models/multiplier-suggestions` (ADR-0123 mục 7): hệ số đang lưu cạnh hệ số hoà vốn của
 * từng chiều = giá vốn chiều đó / đơn giá USD cho 1 triệu token Fox. Giá vốn là USD cho 1 triệu token thật.
 */
export interface AiModelMultiplierSuggestion {
  model_id: string;
  key: string;
  label: string;
  is_active: boolean;
  price_model_name: string | null;
  he_so_dau_vao: number;
  /** `null` là bằng `he_so_dau_vao`. */
  he_so_cache: number | null;
  he_so_dau_ra: number;
  /** Ba hệ số hoà vốn; `null` khi `status` khác `OK`, hoặc riêng một chiều thiếu giá vốn dù `status` là `OK`. */
  hoa_von_dau_vao: number | null;
  hoa_von_cache: number | null;
  hoa_von_dau_ra: number | null;
  status: TrangThaiGoiYHeSo;
  input_usd_per_million: string | null;
  cache_read_usd_per_million: string | null;
  output_usd_per_million: string | null;
  price_id: number | null;
  model_pattern: string | null;
  currency: string | null;
}

export interface AiModelMultiplierSuggestionList {
  don_gia_usd_moi_trieu_token_fox: string | null;
  items: AiModelMultiplierSuggestion[];
}

/** Thân `PATCH /api/v1/ai-models/{id}`: chỉ gửi trường đổi. `key`, `engine` không sửa được. */
export type AiModelUpdate = Partial<Omit<AiModelCreate, 'engine'>>;

/** Một dòng của `GET /api/v1/tenants/{id}/ai-models`: model đang bật của danh mục và tenant có dùng được không. */
export interface TenantAiModel {
  model: AiModel;
  enabled: boolean;
}
